import {createAsyncThunk, createEntityAdapter, createSlice} from "@reduxjs/toolkit";
import {apiRequest} from "../api/apiRequest";
import type {RootState} from "./store";

type AsyncStatus = "idle" | "loading" | "succeeded" | "failed";

/** Matches backend PublicPlanResponse | InternalPlanResponse (GET /plans). */
export interface Plan {
  id: string;
  name: string;
  monthlyBasePriceInCents: number;
  monthlyPricePerEmployeeInCents?: number;
  customerFacingDescription?: string;
  internalDescription?: string;
  customerCanPurchase?: boolean;
}

const plansAdapter = createEntityAdapter<Plan>({
  sortComparer: (a, b) => a.name.localeCompare(b.name),
});

export type PlansState = ReturnType<typeof plansAdapter.getInitialState> & {
  status: AsyncStatus;
  error: string | null;
};

const initialState: PlansState = plansAdapter.getInitialState({
  status: "idle",
  error: null,
});

export const fetchPlans = createAsyncThunk<Plan[], void>("plans/fetchPlans", async () => {
  return apiRequest<Plan[]>({path: "admin/plans"});
});

export const plansSlice = createSlice({
  name: "plans",
  initialState,
  reducers: {
    reset(state) {
      plansAdapter.removeAll(state);
      state.status = "idle";
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPlans.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchPlans.fulfilled, (state, action) => {
        state.status = "succeeded";
        plansAdapter.setAll(state, action.payload);
      })
      .addCase(fetchPlans.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message ?? "Failed to load plans";
      });
  },
});

export const {reset} = plansSlice.actions;

export const plansSelectors = plansAdapter.getSelectors<RootState>((s) => s.plans);
